import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { toMessage } from "@/api/errors";
import {
  linkFolderEmployee,
  listEmployees,
  unlinkFolderEmployee,
} from "@/api/lookups";
import type { EmployeeSummary, Folder, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

/**
 * `FolderEmployeesPanel` — admin sub-panel that lets the admin curate
 * the folder ↔ employee link table.
 *
 * Backend reality check: there is no GET endpoint for `folder_employees`
 * (the link table is write-only in v1 — meetings carry their own
 * `folder_id` directly, and the link table is opaque metadata for
 * future matching heuristics). So this panel cannot honestly render
 * «which employees are in this folder» — it can only offer the
 * link/unlink primitives and surface the operator's chosen list as
 * local state until the next GET endpoint ships in v2.
 *
 * UX consequence: we render the full Employees registry as the
 * candidate list, with a per-row «Связать с этой папкой» / «Отвязать»
 * button. Idempotency is delegated to the backend — POST `/employees`
 * on an already-linked pair is a no-op (or 409, depending on the FE's
 * read of the schema; either way the UI surfaces the message verbatim).
 *
 * This trade-off is documented in `docs/RUNBOOK_UI.md` under "Folders
 * admin → Employees panel" (follow-up item).
 */
interface FolderEmployeesPanelProps {
  folder: Folder;
}

export function FolderEmployeesPanel({ folder }: FolderEmployeesPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState(false);
  // Local "I have linked this employee" memory keyed by employee id.
  // Cleared on unlink; never persisted — see component docstring.
  const [recentlyLinked, setRecentlyLinked] = useState<Set<UUID>>(new Set());
  const [actionError, setActionError] = useState<Record<string, string>>({});

  const employees = useQuery({
    queryKey: ["employees", "list"],
    queryFn: () => listEmployees(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ["meetings", "folder", folder.id],
    });
  };

  const unlinkMutation = useMutation({
    mutationFn: (employeeId: UUID) =>
      unlinkFolderEmployee(folder.id, employeeId),
    onSuccess: (_data, employeeId) => {
      setRecentlyLinked((prev) => {
        const next = new Set(prev);
        next.delete(employeeId);
        return next;
      });
      invalidate();
    },
  });

  const linkMutation = useMutation({
    mutationFn: (employeeId: UUID) => linkFolderEmployee(folder.id, employeeId),
    onSuccess: (_data, employeeId) => {
      setRecentlyLinked((prev) => new Set(prev).add(employeeId));
      setLinking(false);
      invalidate();
    },
  });

  const linkedList: EmployeeSummary[] = useMemo(() => {
    const all = employees.data ?? [];
    return all
      .filter((e) => recentlyLinked.has(e.id))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, "ru"));
  }, [employees.data, recentlyLinked]);

  const handleUnlink = (employeeId: UUID) => {
    const ok = window.confirm(t("folders.admin.unlinkEmployeeConfirm"));
    if (!ok) return;
    setActionError((prev) => ({ ...prev, [employeeId]: "" }));
    unlinkMutation.mutate(employeeId, {
      onError: (err: unknown) => {
        setActionError((prev) => ({
          ...prev,
          [employeeId]: toMessage(
            err,
            t("folders.admin.errors.unlinkEmployeeFailed"),
          ),
        }));
      },
    });
  };

  return (
    <section className="space-y-3" data-testid="folder-employees-panel">
      <header className="flex items-end justify-between gap-2">
        <h3 className="text-base font-semibold">
          {t("folders.admin.employeesHeader")}
        </h3>
        <Button
          type="button"
          size="sm"
          onClick={() => setLinking(true)}
          data-testid="open-link-employee"
        >
          {t("folders.admin.addEmployee")}
        </Button>
      </header>

      {employees.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : linkedList.length === 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="folder-employees-empty"
        >
          {t("folders.admin.noEmployees")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table
            className="w-full text-sm"
            data-testid="folder-employees-table"
          >
            <thead className="bg-muted/40">
              <tr>
                <th className="border-b px-3 py-2 text-left font-medium">
                  {t("folders.admin.employeeColumns.employee")}
                </th>
                <th className="border-b px-3 py-2 text-left font-medium">
                  {t("folders.admin.employeeColumns.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {linkedList.map((e) => {
                const err = actionError[e.id] ?? "";
                return (
                  <tr key={e.id} data-testid={`folder-employee-row-${e.id}`}>
                    <td className="border-b px-3 py-2 align-top">
                      {e.full_name}
                    </td>
                    <td className="border-b px-3 py-2 align-top">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnlink(e.id)}
                        disabled={unlinkMutation.isPending}
                        data-testid={`folder-employee-unlink-${e.id}`}
                      >
                        {t("folders.admin.unlinkEmployee")}
                      </Button>
                      {err !== "" ? (
                        <p
                          className="mt-1 text-xs text-destructive"
                          role="alert"
                          data-testid={`folder-employee-unlink-error-${e.id}`}
                        >
                          {err}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LinkEmployeeDialog
        open={linking}
        onClose={() => setLinking(false)}
        candidates={(employees.data ?? []).filter(
          (e) => !recentlyLinked.has(e.id),
        )}
        isPending={linkMutation.isPending}
        error={
          linkMutation.isError
            ? toMessage(
                linkMutation.error,
                t("folders.admin.errors.linkEmployeeFailed"),
              )
            : null
        }
        onSubmit={(employeeId) => linkMutation.mutate(employeeId)}
      />
    </section>
  );
}

interface LinkEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (employeeId: UUID) => void;
  candidates: EmployeeSummary[];
  isPending: boolean;
  error: string | null;
}

function LinkEmployeeDialog({
  open,
  onClose,
  onSubmit,
  candidates,
  isPending,
  error,
}: LinkEmployeeDialogProps) {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState<UUID | "">("");

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("folders.admin.addEmployee")}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (employeeId === "") return;
            onSubmit(employeeId);
          }}
          data-testid="folder-employee-link-form"
        >
          <div>
            <Label htmlFor="folder-employee-pick">
              {t("folders.admin.employeeLabel")}
            </Label>
            <select
              id="folder-employee-pick"
              data-testid="folder-employee-select"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value as UUID | "")}
              className="mt-1 w-full rounded-md border px-2 py-1 text-sm"
              required
            >
              <option value="">—</option>
              {candidates.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name}
                </option>
              ))}
            </select>
          </div>
          {error !== null ? (
            <p
              className="text-xs text-destructive"
              role="alert"
              data-testid="folder-employee-link-error"
            >
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isPending || employeeId === ""}
              data-testid="submit-link-employee"
            >
              {isPending ? t("common.saving") : t("folders.admin.addEmployee")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
